import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { AuthSessionService } from '@/core/services/auth-session.service';
import { WebsocketService } from '@/core/services/websocket/websocket.service';
import { NotificationsService } from '@/core/services/notifications.service';
import {
  NotificationFeedRow,
  NotificationsItem,
  NotificationsPreview,
} from '@/core/models/notification';

const PAGE_SIZE = 50;

const EMPTY_PREVIEW: NotificationsPreview = {
  lastMessage: null,
  lastMessageAt: null,
  unread: 0,
};

function dayKey(date?: string | null): string {
  if (!date) {
    return '';
  }
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

@Injectable({ providedIn: 'root' })
export class NotificationsStore {
  private readonly api = inject(NotificationsService);
  private readonly authSession = inject(AuthSessionService);
  private readonly websocket = inject(WebsocketService);

  private readonly itemsSignal = signal<NotificationsItem[]>([]);
  private readonly loadingSignal = signal(false);
  private readonly loadingNextSignal = signal(false);
  private readonly firstLoadedSignal = signal(false);
  private readonly pageNumberSignal = signal(0);
  private readonly totalPagesSignal = signal(0);
  private fetchGen = 0;
  private readonly readIds = new Set<number>();

  readonly items = this.itemsSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly loadingNext = this.loadingNextSignal.asReadonly();
  readonly firstLoaded = this.firstLoadedSignal.asReadonly();

  readonly preview = computed<NotificationsPreview>(() => {
    const data = this.itemsSignal();
    const last = data[0];
    if (!last) {
      return EMPTY_PREVIEW;
    }
    return {
      lastMessage: last.body ?? last.title ?? null,
      lastMessageAt: last.createdAt ?? null,
      unread: data.filter((item) => !item.readAt).length,
    };
  });

  readonly feed = computed<NotificationFeedRow[]>(() => {
    const chronological = [...this.itemsSignal()].reverse();
    return chronological.map((item, index) => ({
      item,
      showDivider:
        index === 0 || dayKey(item.createdAt) !== dayKey(chronological[index - 1]?.createdAt),
    }));
  });

  constructor() {
    effect(() => {
      const authenticated = this.authSession.isAuthenticated();
      untracked(() => {
        if (authenticated) {
          this.loadInitial();
        } else {
          this.reset();
        }
      });
    });

    this.websocket.notificationEvents$.subscribe(() => {
      if (this.authSession.isAuthenticated()) {
        this.reload();
      }
    });
  }

  loadInitial(): void {
    if (this.firstLoadedSignal()) {
      this.reload();
      return;
    }
    this.fetchPage(0, { replace: true, showLoading: true });
  }

  reload(after?: () => void): void {
    this.fetchPage(0, { replace: true, showLoading: !this.firstLoadedSignal(), after });
  }

  loadNext(after?: () => void): void {
    if (this.loadingSignal() || this.loadingNextSignal()) {
      return;
    }
    if (!this.canLoadNext()) {
      return;
    }
    this.fetchPage(this.pageNumberSignal() + 1, { replace: false, showLoading: false, after });
  }

  canLoadNext(): boolean {
    return this.firstLoadedSignal() && this.pageNumberSignal() + 1 < this.totalPagesSignal();
  }

  markUnreadAsRead(): void {
    const current = this.itemsSignal();
    let changed = false;
    const next = current.map((item) => {
      if (item.readAt) {
        return item;
      }
      if (this.readIds.has(item.id)) {
        changed = true;
        return { ...item, readAt: new Date().toISOString() };
      }
      this.readIds.add(item.id);
      this.api.markRead(item.id).subscribe({
        next: (updated) => this.patchItem(updated),
        error: () => {
          this.readIds.delete(item.id);
          this.patchItem({ ...item, readAt: null });
        },
      });
      changed = true;
      return { ...item, readAt: new Date().toISOString() };
    });
    if (changed) {
      this.itemsSignal.set(next);
    }
  }

  private fetchPage(
    page: number,
    options: { replace: boolean; showLoading: boolean; after?: () => void }
  ): void {
    const userId = this.authSession.session()?.user?.id;
    if (!userId) {
      options.after?.();
      return;
    }

    const gen = options.replace ? ++this.fetchGen : this.fetchGen;

    if (options.replace) {
      if (options.showLoading) {
        this.loadingSignal.set(true);
      }
    } else {
      this.loadingNextSignal.set(true);
    }

    this.api
      .getByParams({
        userId,
        sort: 'createdAt',
        direction: 'DESC',
        page,
        size: PAGE_SIZE,
      })
      .subscribe({
        next: (response) => {
          if (options.replace && gen !== this.fetchGen) {
            return;
          }
          const content = response?.content ?? [];
          const pageNumber = response?.pageable?.pageNumber ?? response?.number ?? page;
          this.pageNumberSignal.set(pageNumber);
          this.totalPagesSignal.set(response?.totalPages ?? 0);
          this.firstLoadedSignal.set(true);
          this.loadingSignal.set(false);
          this.loadingNextSignal.set(false);

          if (options.replace) {
            this.itemsSignal.set(content);
          } else {
            const existing = new Set(this.itemsSignal().map((item) => item.id));
            this.itemsSignal.update((current) => [
              ...current,
              ...content.filter((item) => !existing.has(item.id)),
            ]);
          }
          options.after?.();
        },
        error: () => {
          if (options.replace && gen !== this.fetchGen) {
            return;
          }
          this.firstLoadedSignal.set(true);
          this.loadingSignal.set(false);
          this.loadingNextSignal.set(false);
          options.after?.();
        },
      });
  }

  private patchItem(updated: NotificationsItem): void {
    if (!updated?.id) {
      return;
    }
    this.itemsSignal.update((current) =>
      current.map((item) => (item.id === updated.id ? { ...item, ...updated } : item))
    );
  }

  private reset(): void {
    this.itemsSignal.set([]);
    this.loadingSignal.set(false);
    this.loadingNextSignal.set(false);
    this.firstLoadedSignal.set(false);
    this.pageNumberSignal.set(0);
    this.totalPagesSignal.set(0);
    this.readIds.clear();
    this.fetchGen += 1;
  }
}
